import { useState, useEffect } from 'react';

/**
 * Hook para detectar características do dispositivo
 * Útil para adaptar a interface entre desktop e mobile
 */
export function useDevice() {
  const [device, setDevice] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    isIOS: false,
    isAndroid: false,
    isStandalone: false,
    touch: false,
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const checkDevice = () => {
      const userAgent = navigator.userAgent;
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // Detecção de plataforma
      const isIOS = /iPad|iPhone|iPod/.test(userAgent) || 
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const isAndroid = /Android/.test(userAgent);
      const isMobile = isIOS || isAndroid || width < 1024;
      const isTablet = (isIOS && width >= 768 && width < 1024) || (isAndroid && width >= 600 && width < 1024);
      const isDesktop = !isMobile && !isTablet;
      
      // PWA standalone mode
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      
      // Touch support
      const touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

      setDevice({
        isMobile,
        isTablet,
        isDesktop,
        isIOS,
        isAndroid,
        isStandalone,
        touch,
        width,
        height,
      });
    };

    // Check inicial
    checkDevice();

    // Listener para resize
    const handleResize = () => {
      checkDevice();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return device;
}

/**
 * Hook específico para media queries
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [matches, query]);

  return matches;
}

/**
 * Hook para detectar se está em modo landscape
 */
export function useOrientation() {
  const [orientation, setOrientation] = useState({
    isLandscape: window.innerWidth > window.innerHeight,
    isPortrait: window.innerWidth <= window.innerHeight,
  });

  useEffect(() => {
    const handleOrientationChange = () => {
      setOrientation({
        isLandscape: window.innerWidth > window.innerHeight,
        isPortrait: window.innerWidth <= window.innerHeight,
      });
    };

    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  return orientation;
}

/**
 * Hook para safe areas (notch, dynamic island, home indicator)
 */
export function useSafeArea() {
  const [safeArea, setSafeArea] = useState({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  });

  useEffect(() => {
    // Verifica se suporta env(safe-area-inset-*)
    const style = getComputedStyle(document.documentElement);
    
    const updateSafeArea = () => {
      // Para iOS PWA, usamos os valores CSS env()
      setSafeArea({
        top: parseInt(style.getPropertyValue('--sat') || 0, 10),
        bottom: parseInt(style.getPropertyValue('--sab') || 0, 10),
        left: parseInt(style.getPropertyValue('--sal') || 0, 10),
        right: parseInt(style.getPropertyValue('--sar') || 0, 10),
      });
    };

    updateSafeArea();
    
    // Atualiza em resize/orientation change
    window.addEventListener('resize', updateSafeArea);
    return () => window.removeEventListener('resize', updateSafeArea);
  }, []);

  return safeArea;
}

export default useDevice;
